<template>
  <div class="bons-manager">
    <!-- Onglets Sortie/Retour -->
    <div class="tabs-header">
      <button 
        :class="{ active: activeTab === 'sortie' }"
        @click="activeTab = 'sortie'"
        class="tab-btn"
      >
        <i class="fas fa-arrow-right"></i>
        Sortie
        <span class="badge" v-if="bonsSortie.length">{{ bonsSortie.length }}</span>
      </button>
      <button 
        :class="{ active: activeTab === 'retour' }"
        @click="activeTab = 'retour'"
        class="tab-btn"
      >
        <i class="fas fa-arrow-left"></i>
        Entrée
        <span class="badge" v-if="bonsRetour.length">{{ bonsRetour.length }}</span>
      </button>
    </div>

    <!-- Contenu des onglets -->
    <div class="tab-content">
      <!-- Bons de Sortie -->
      <div v-if="activeTab === 'sortie'" class="sortie-content">
        <div class="toolbar">
          <div class="toolbar-left">
            <div class="filter-wrapper filter-status-bons">
              <div class="filter-icon">
                <i class="fas fa-flag"></i>
              </div>
              <select v-model="filters.statut" @change="applyFilters" class="filter-select premium-select">
                <option value="">Tous statuts</option>
                <option value="en_attente_signature">En attente signature</option>
                <option value="signe">Signé</option>
                <option value="partiellement_retourne">Partiellement retourné</option>
                <option value="entierement_retourne">Entièrement retourné</option>
              </select>
              <div class="filter-arrow">
                <i class="fas fa-chevron-down"></i>
              </div>
            </div>
          </div>
          <div class="toolbar-right">
            <button @click="$emit('create', 'sortie')" class="btn btn-primary btn-nouveau-bon">
              <i class="fas fa-plus"></i>
              Nouveau Bon de Sortie
            </button>
          </div>
        </div>

        <div class="report-content">
          <div v-for="item in filteredBonsSortie" :key="item.technicien._id" class="technicien-card">
            <div class="card-header" @click="toggleDetails(item.technicien._id)">
              <div class="tech-info">
                <div class="tech-avatar">
                  <i class="fas fa-user-cog"></i>
                </div>
                <div class="tech-details">
                  <h3>{{ item.technicien.prenom }} {{ item.technicien.nom }}</h3>
                  <p>{{ item.technicien.email }}</p>
                </div>
              </div>
              
              <div class="tech-stats">
                <div class="stat-item">
                  <div class="stat-icon en-sac">
                    <i class="fas fa-toolbox"></i>
                  </div>
                  <div class="stat-content">
                    <span class="stat-value">{{ item.produitsEnSac }}</span>
                    <span class="stat-label">En sac</span>
                  </div>
                </div>
                
                <div class="stat-item">
                  <div class="stat-icon affectes">
                    <i class="fas fa-check-circle"></i>
                  </div>
                  <div class="stat-content">
                    <span class="stat-value">{{ item.produitsAffectes }}</span>
                    <span class="stat-label">Affectés</span>
                  </div>
                </div>
                
                <div class="stat-item">
                  <div class="stat-icon retournes">
                    <i class="fas fa-undo"></i>
                  </div>
                  <div class="stat-content">
                    <span class="stat-value">{{ item.produitsRetournes }}</span>
                    <span class="stat-label">Retournés</span>
                  </div>
                </div>
                
                <button @click.stop="exportTechnicienPDF(item)" class="stat-item stat-export-btn" title="Gérer le bon de sortie">
                  <div class="stat-icon export-pdf">
                    <i class="fas fa-file-pdf"></i>
                  </div>
                  <div class="stat-content">
                    <span class="stat-label">Gérer Bon de Sortie</span>
                  </div>
                </button>
              </div>

              <button class="toggle-btn">
                <i class="fas" :class="expandedBons.includes(item.technicien._id) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
              </button>
            </div>

            <transition name="slide">
              <div v-if="expandedBons.includes(item.technicien._id)" class="card-details">
                <div class="details-header">
                  <h4><i class="fas fa-boxes"></i> Produits en sac ({{ item.details.length }})</h4>
                </div>
                
                <div v-if="item.details.length === 0" class="no-products">
                  <i class="fas fa-inbox"></i>
                  <p>Aucun produit dans le sac</p>
                </div>

                <div v-else class="products-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Référence</th>
                        <th>Désignation</th>
                        <th>Catégorie</th>
                        <th>Quantité</th>
                        <th>Statut</th>
                        <th>Date Affectation</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="product in item.details" :key="product._id">
                        <td><strong>{{ product.referenceInterne }}</strong></td>
                        <td>{{ product.designation }}</td>
                        <td>
                          <span class="category-badge">{{ product.categorie }}</span>
                        </td>
                        <td>
                          <span class="quantity-badge">{{ product.quantite }}</span>
                        </td>
                        <td>
                          <span class="status-badge" :class="getStatusClass(product.statut)">
                            {{ getStatusLabel(product.statut) }}
                          </span>
                        </td>
                        <td>{{ formatDate(product.dateCreation) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>

      <!-- Bons de Retour -->
      <div v-if="activeTab === 'retour'" class="retour-content">
        <div class="toolbar">
          <div class="toolbar-left">
            <div class="filter-wrapper filter-status-bons">
              <div class="filter-icon">
                <i class="fas fa-flag"></i>
              </div>
              <select v-model="filters.statut" @change="applyFilters" class="filter-select premium-select">
                <option value="">Tous statuts</option>
                <option value="en_attente_verification">En attente vérification</option>
                <option value="verifie">Vérifié</option>
                <option value="accepte">Accepté</option>
                <option value="refuse_partiel">Refusé partiel</option>
              </select>
              <div class="filter-arrow">
                <i class="fas fa-chevron-down"></i>
              </div>
            </div>
          </div>
          <button @click="openBonRetourModal" class="btn btn-primary btn-nouveau-bon">
            <i class="fas fa-plus"></i>
            Nouveau Bon de Retour
          </button>
        </div>

        <div class="report-content">
          <div v-for="item in filteredBonsRetour" :key="item.technicien._id" class="technicien-card">
            <div class="card-header" @click="toggleDetails('retour_' + item.technicien._id)">
              <div class="tech-info">
                <div class="tech-avatar">
                  <i class="fas fa-user-cog"></i>
                </div>
                <div class="tech-details">
                  <h3>{{ item.technicien.prenom }} {{ item.technicien.nom }}</h3>
                  <p>{{ item.technicien.email }}</p>
                </div>
              </div>
              
              <div class="tech-stats">
                <div class="stat-item">
                  <div class="stat-icon retour-total">
                    <i class="fas fa-undo-alt"></i>
                  </div>
                  <div class="stat-content">
                    <span class="stat-value">{{ item.totalProduits }}</span>
                    <span class="stat-label">À retourner</span>
                  </div>
                </div>
                
                <div class="stat-item">
                  <div class="stat-icon retour-affecte">
                    <i class="fas fa-user-tag"></i>
                  </div>
                  <div class="stat-content">
                    <span class="stat-value">{{ item.parStatut.affecte_technicien }}</span>
                    <span class="stat-label">Affectés</span>
                  </div>
                </div>
                
                <div class="stat-item">
                  <div class="stat-icon retour-encours">
                    <i class="fas fa-spinner"></i>
                  </div>
                  <div class="stat-content">
                    <span class="stat-value">{{ item.parStatut.en_cours }}</span>
                    <span class="stat-label">En cours</span>
                  </div>
                </div>
                
                <div class="stat-item">
                  <div class="stat-icon retour-reserve">
                    <i class="fas fa-bookmark"></i>
                  </div>
                  <div class="stat-content">
                    <span class="stat-value">{{ item.parStatut.reserve }}</span>
                    <span class="stat-label">Réservés</span>
                  </div>
                </div>
                
                <button @click.stop="exportRetourPDF(item)" class="stat-item stat-export-btn" title="Gérer le bon de retour">
                  <div class="stat-icon export-pdf">
                    <i class="fas fa-file-pdf"></i>
                  </div>
                  <div class="stat-content">
                    <span class="stat-label">Gérer Bon de Retour</span>
                  </div>
                </button>
              </div>

              <button class="toggle-btn">
                <i class="fas" :class="expandedBons.includes('retour_' + item.technicien._id) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
              </button>
            </div>

            <transition name="slide">
              <div v-if="expandedBons.includes('retour_' + item.technicien._id)" class="card-details">
                <div class="details-header">
                  <h4><i class="fas fa-undo-alt"></i> Produits à retourner ({{ item.details.length }})</h4>
                  <div class="details-actions">
                    <button @click.stop="createReturnBon(item)" class="btn-action btn-create">
                      <i class="fas fa-plus"></i> Créer bon de retour
                    </button>
                  </div>
                </div>
                
                <div v-if="item.details.length === 0" class="no-products">
                  <i class="fas fa-inbox"></i>
                  <p>Aucun produit à retourner</p>
                </div>

                <div v-else class="products-table">
                  <!-- Actions de sélection -->
                  <div class="selection-actions">
                    <label class="select-all-checkbox">
                      <input 
                        type="checkbox" 
                        @change="toggleSelectAllProducts(item.technicien._id, $event.target.checked)"
                        :checked="areAllProductsSelected(item.technicien._id, item.details)"
                      >
                      Sélectionner tout ({{ getSelectedProductsCount(item.technicien._id) }}/{{ item.details.length }})
                    </label>
                    <button 
                      v-if="getSelectedProductsCount(item.technicien._id) > 0"
                      @click="exportSelectedProductsPDF(item)"
                      class="btn btn-primary btn-sm"
                    >
                      <i class="fas fa-file-pdf"></i>
                      Exporter PDF ({{ getSelectedProductsCount(item.technicien._id) }})
                    </button>
                  </div>

                  <table>
                    <thead>
                      <tr>
                        <th style="width: 40px;">
                          <i class="fas fa-check-square"></i>
                        </th>
                        <th>Référence</th>
                        <th>Désignation</th>
                        <th>Catégorie</th>
                        <th>Quantité</th>
                        <th>Statut</th>
                        <th>Emplacement</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="product in item.details" :key="product._id">
                        <td>
                          <input 
                            type="checkbox" 
                            :value="product._id"
                            v-model="selectedProductsByTech[item.technicien._id]"
                            class="product-checkbox"
                          >
                        </td>
                        <td><strong>{{ product.referenceInterne }}</strong></td>
                        <td>{{ product.designation }}</td>
                        <td>
                          <span class="category-badge">{{ product.categorie }}</span>
                        </td>
                        <td>
                          <span class="quantity-badge">{{ product.quantite }}</span>
                        </td>
                        <td>
                          <span class="status-badge" :class="getStatusClass(product.statut)">
                            {{ getStatusLabel(product.statut) }}
                          </span>
                        </td>
                        <td>{{ formatEmplacement(product.emplacement) }}</td>
                        <td>
                          <button 
                            @click="returnToStock(product)" 
                            class="btn-return-stock"
                            title="Retourner au stock"
                          >
                            <i class="fas fa-undo"></i>
                            Retourner
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Bon de Retour -->
    <BonRetourModal 
      v-if="showBonRetourModal" 
      @close="closeBonRetourModal"
      @created="onBonRetourCreated"
    />

    <!-- Modal Matricule Voiture -->
    <MatriculeVoitureModal
      v-if="showMatriculeModal"
      @close="closeMatriculeModal"
      @confirm="onMatriculeConfirm"
    />
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import BonRetourModal from './BonRetourModal.vue'
import MatriculeVoitureModal from './MatriculeVoitureModal.vue'

export default {
  name: 'BonsManager',
  components: {
    MatriculeVoitureModal,
    BonRetourModal
  },
  props: {
    bons: {
      type: Object,
      default: () => ({ sortie: [], retour: [] })
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['refresh', 'create', 'sign', 'verify', 'view', 'print', 'create-return', 'return-to-stock'],
  setup(props, { emit }) {
    console.log('🚀 BonsManager CHARGÉ - Version avec bouton Export PDF individuel')
    
    // Récupérer le store Vuex
    const store = useStore()
    
    // Debug: Surveiller les changements de props.bons
    watch(() => props.bons, (newBons) => {
      console.log('🔍 BonsManager - Données reçues:', newBons)
      console.log('📦 Bons de sortie:', newBons.sortie?.length || 0)
      console.log('🔙 Bons de retour:', newBons.retour?.length || 0)
    }, { immediate: true, deep: true })
    const activeTab = ref('sortie')
    const filters = ref({
      statut: '',
      technicien: ''
    })
    const expandedBons = ref([])
    const showBonRetourModal = ref(false)
    const selectedProductsByTech = ref({})
    const showMatriculeModal = ref(false)
    const matriculeVoiture = ref('')
    const pendingTechnicienData = ref(null)

    const bonsSortie = computed(() => props.bons.sortie || [])
    const bonsRetour = computed(() => props.bons.retour || [])

    // Initialiser les tableaux de sélection pour chaque technicien
    watch(bonsRetour, (newBonsRetour) => {
      newBonsRetour.forEach(item => {
        if (!selectedProductsByTech.value[item.technicien._id]) {
          selectedProductsByTech.value[item.technicien._id] = []
        }
      })
    }, { immediate: true, deep: true })

    const filteredBonsSortie = computed(() => {
      let result = [...bonsSortie.value]
      if (filters.value.statut) {
        result = result.filter(bon => bon.statut === filters.value.statut)
      }
      return result.sort((a, b) => new Date(b.dateHeureSortie) - new Date(a.dateHeureSortie))
    })

    const filteredBonsRetour = computed(() => {
      let result = [...bonsRetour.value]
      if (filters.value.statut) {
        result = result.filter(bon => bon.statut === filters.value.statut)
      }
      return result.sort((a, b) => new Date(b.dateHeureRetour) - new Date(a.dateHeureRetour))
    })

    const applyFilters = () => {
      // Filtres appliqués automatiquement via computed
    }

    const getStatusClass = (statut) => {
      const classes = {
        'en_attente_signature': 'status-pending',
        'signe': 'status-signed',
        'partiellement_retourne': 'status-partial',
        'entierement_retourne': 'status-complete',
        'en_attente_verification': 'status-pending',
        'verifie': 'status-verified',
        'accepte': 'status-accepted',
        'refuse_partiel': 'status-partial-refused'
      }
      return classes[statut] || 'status-default'
    }

    const getStatusLabel = (statut) => {
      const labels = {
        'en_attente_signature': 'En attente signature',
        'signe': 'Signé',
        'partiellement_retourne': 'Partiellement retourné',
        'entierement_retourne': 'Entièrement retourné',
        'en_attente_verification': 'En attente vérification',
        'verifie': 'Vérifié',
        'accepte': 'Accepté',
        'refuse_partiel': 'Refusé partiel'
      }
      return labels[statut] || statut
    }

    const getTypeLabel = (type) => {
      const labels = {
        'retour_complet': 'Retour complet',
        'retour_partiel': 'Retour partiel',
        'retour_defectueux': 'Retour défectueux'
      }
      return labels[type] || type
    }

    const formatDate = (date) => {
      return new Intl.DateTimeFormat('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(date))
    }

    const formatPrice = (price) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'TND'
      }).format(price || 0)
    }

    const viewBon = (bon) => {
      emit('view', bon)
    }

    const signBon = (bon) => {
      emit('sign', bon)
    }

    const verifyBon = (bon) => {
      emit('verify', bon)
    }

    const printBon = (bon) => {
      emit('print', bon)
    }

    const toggleDetails = (bonId) => {
      const index = expandedBons.value.indexOf(bonId)
      if (index > -1) {
        expandedBons.value.splice(index, 1)
      } else {
        expandedBons.value.push(bonId)
      }
    }

    const formatEmplacement = (emplacement) => {
      if (!emplacement) return '-'
      // Remplacer les underscores par des espaces et capitaliser
      return emplacement
        .replace(/_/g, ' ')
        .replace(/Technicien/i, 'Technicien')
        .replace(/magasin central/i, 'Magasin Central')
        .replace(/intervention/i, 'Intervention')
    }

    const createReturnBon = (item) => {
      // Émettre un événement pour créer un bon de retour avec les produits du technicien
      emit('create-return', {
        technicien: item.technicien,
        produits: item.details
      })
    }

    const returnToStock = (product) => {
      // Émettre un événement pour retourner le produit au stock
      emit('return-to-stock', product)
    }

    const openBonRetourModal = () => {
      showBonRetourModal.value = true
    }

    const closeBonRetourModal = () => {
      showBonRetourModal.value = false
    }

    const onBonRetourCreated = () => {
      // Rafraîchir les données après création du bon
      emit('refresh')
      showBonRetourModal.value = false
    }

    // Méthodes pour la sélection de produits
    const toggleSelectAllProducts = (technicienId, selectAll) => {
      if (!selectedProductsByTech.value[technicienId]) {
        selectedProductsByTech.value[technicienId] = []
      }
      
      if (selectAll) {
        // Sélectionner tous les produits du technicien
        const technicienData = bonsRetour.value.find(item => item.technicien._id === technicienId)
        if (technicienData) {
          selectedProductsByTech.value[technicienId] = technicienData.details.map(product => product._id)
        }
      } else {
        // Désélectionner tous
        selectedProductsByTech.value[technicienId] = []
      }
    }

    const areAllProductsSelected = (technicienId, products) => {
      if (!selectedProductsByTech.value[technicienId] || !Array.isArray(selectedProductsByTech.value[technicienId]) || products.length === 0) {
        return false
      }
      return products.every(product => selectedProductsByTech.value[technicienId].includes(product._id))
    }

    const getSelectedProductsCount = (technicienId) => {
      return selectedProductsByTech.value[technicienId]?.length || 0
    }

    const exportSelectedProductsPDF = (item) => {
      const selectedProductIds = selectedProductsByTech.value[item.technicien._id] || []
      if (selectedProductIds.length === 0) {
        alert('Veuillez sélectionner au moins un produit')
        return
      }

      // Filtrer les produits sélectionnés
      const selectedProducts = item.details.filter(product => selectedProductIds.includes(product._id))
      
      // Créer un objet temporaire avec seulement les produits sélectionnés
      const itemWithSelectedProducts = {
        ...item,
        details: selectedProducts
      }

      // Utiliser la fonction d'export existante
      exportRetourPDF(itemWithSelectedProducts)
    }

    const getStatusIcon = (statut) => {
      const icons = {
        'en_attente_signature': 'fa-clock',
        'signe': 'fa-check-circle',
        'partiellement_retourne': 'fa-undo',
        'entierement_retourne': 'fa-check-double',
        'annule': 'fa-times-circle'
      }
      return icons[statut] || 'fa-question-circle'
    }

    const getStatusIconClass = (statut) => {
      const classes = {
        'en_attente_signature': 'status-pending',
        'signe': 'status-signed',
        'partiellement_retourne': 'status-partial',
        'entierement_retourne': 'status-complete',
        'annule': 'status-cancelled'
      }
      return classes[statut] || ''
    }

    const openMatriculeModal = (item) => {
      pendingTechnicienData.value = item
      matriculeVoiture.value = ''
      showMatriculeModal.value = true
    }

    const closeMatriculeModal = () => {
      showMatriculeModal.value = false
      matriculeVoiture.value = ''
      pendingTechnicienData.value = null
    }

    const confirmMatricule = () => {
      if (!matriculeVoiture.value || matriculeVoiture.value.trim() === '') {
        alert('Veuillez saisir le matricule de la voiture')
        return
      }
      
      // Générer le PDF avec le matricule
      generatePDFWithMatricule(pendingTechnicienData.value, matriculeVoiture.value.trim())
      closeMatriculeModal()
    }

    // Nouvelle fonction pour le composant modal séparé
    const onMatriculeConfirm = (matricule) => {
      if (matricule && pendingTechnicienData.value) {
        generatePDFWithMatricule(pendingTechnicienData.value, matricule)
        closeMatriculeModal()
      }
    }

    const exportTechnicienPDF = (item) => {
      // Ouvrir le modal pour demander le matricule
      openMatriculeModal(item)
    }

    const generatePDFWithMatricule = (item, matricule) => {
      try {
        console.log('🔍 Export PDF pour:', item.technicien)
        console.log('📦 Produits:', item.details)
        console.log('🚗 Matricule:', matricule)
        
        // Créer un PDF pour un technicien spécifique
        const doc = new jsPDF()
        console.log('📄 jsPDF instance créée:', doc)
        console.log('🔧 autoTable disponible?', typeof doc.autoTable)
          
        
        // ========== EN-TÊTE AVEC LOGO AMANA ==========
        // Logo Amana (texte stylisé)
        doc.setFontSize(28)
        doc.setTextColor(0, 27, 183)
        doc.setFont('helvetica', 'bold')
        doc.text('AMANA', 105, 20, { align: 'center' })
        
        doc.setFontSize(10)
        doc.setTextColor(100, 100, 100)
        doc.setFont('helvetica', 'normal')
        doc.text('MANAGEMENT SUITE', 105, 27, { align: 'center' })
        
        // Ligne de séparation
        doc.setDrawColor(0, 27, 183)
        doc.setLineWidth(0.5)
        doc.line(20, 32, 190, 32)
        
        // ========== TITRE DU DOCUMENT ==========
        doc.setFontSize(20)
        doc.setTextColor(0, 27, 183)
        doc.setFont('helvetica', 'bold')
        doc.text('BON DE SORTIE', 105, 42, { align: 'center' })
        
        // Numéro et date du bon
        doc.setFontSize(10)
        doc.setTextColor(60, 60, 60)
        doc.setFont('helvetica', 'normal')
        const dateNow = new Date()
        const bonNumber = `BS-${dateNow.getFullYear()}${String(dateNow.getMonth() + 1).padStart(2, '0')}${String(dateNow.getDate()).padStart(2, '0')}-${String(Math.floor(Math.random() * 9999)).padStart(4, '0')}`
        doc.text(`N° ${bonNumber}`, 105, 50, { align: 'center' })
        doc.text(`Date: ${dateNow.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}`, 105, 56, { align: 'center' })
        
        // ========== INFORMATIONS TECHNICIEN ==========
        let yPos = 70
        
        // Cadre technicien (agrandi pour inclure le matricule)
        doc.setFillColor(240, 248, 255)
        doc.roundedRect(20, yPos, 85, 42, 3, 3, 'F')
        doc.setDrawColor(0, 27, 183)
        doc.setLineWidth(0.3)
        doc.roundedRect(20, yPos, 85, 42, 3, 3, 'S')
        
        doc.setFontSize(11)
        doc.setTextColor(0, 27, 183)
        doc.setFont('helvetica', 'bold')
        doc.text('TECHNICIEN', 25, yPos + 7)
        
        doc.setFontSize(10)
        doc.setTextColor(40, 40, 40)
        doc.setFont('helvetica', 'bold')
        doc.text(`${item.technicien.prenom || ''} ${item.technicien.nom || ''}`, 25, yPos + 15)
        
        doc.setFontSize(9)
        doc.setTextColor(80, 80, 80)
        doc.setFont('helvetica', 'normal')
        doc.text(`Email: ${item.technicien.email || 'N/A'}`, 25, yPos + 22)
        doc.text(`Tél: ${item.technicien.telephone || 'N/A'}`, 25, yPos + 28)
        
        // Matricule de la voiture
        doc.setFontSize(9)
        doc.setTextColor(220, 38, 38)
        doc.setFont('helvetica', 'bold')
        doc.text(`Voiture: ${matricule}`, 25, yPos + 33)
        
        // ========== INFORMATIONS MAGASINIER ==========
        // Cadre magasinier (agrandi pour correspondre)
        doc.setFillColor(240, 255, 240)
        doc.roundedRect(110, yPos, 80, 42, 3, 3, 'F')
        doc.setDrawColor(16, 185, 129)
        doc.setLineWidth(0.3)
        doc.roundedRect(110, yPos, 80, 42, 3, 3, 'S')
        
        doc.setFontSize(11)
        doc.setTextColor(16, 185, 129)
        doc.setFont('helvetica', 'bold')
        doc.text('MAGASINIER', 115, yPos + 7)
        
        doc.setFontSize(10)
        doc.setTextColor(40, 40, 40)
        doc.setFont('helvetica', 'bold')
        
        // Récupérer les informations de l'utilisateur connecté depuis Vuex store
        const user = store.state.auth.user || {}
        
        console.log('👤 Utilisateur connecté (depuis Vuex):', user)
        
        const magasinierNom = `${user.prenom || ''} ${user.nom || ''}`.trim() || 'Utilisateur'
        const magasinierRole = user.role === 'Admin' ? 'ADMINISTRATEUR' : 'MAGASINIER'
        
        doc.text(magasinierNom, 115, yPos + 15)
        
        doc.setFontSize(9)
        doc.setTextColor(80, 80, 80)
        doc.setFont('helvetica', 'normal')
        doc.text(`Email: ${user.email || 'N/A'}`, 115, yPos + 22)
        doc.text(`Rôle: ${magasinierRole}`, 115, yPos + 28)
        
        // ========== STATISTIQUES PRODUITS ==========
        yPos = 122
        
        doc.setFillColor(255, 250, 240)
        doc.roundedRect(20, yPos, 170, 20, 3, 3, 'F')
        doc.setDrawColor(245, 158, 11)
        doc.setLineWidth(0.3)
        doc.roundedRect(20, yPos, 170, 20, 3, 3, 'S')
        
        doc.setFontSize(10)
        doc.setTextColor(60, 60, 60)
        doc.setFont('helvetica', 'bold')
        doc.text(`Produits en sac: ${item.produitsEnSac || 0}`, 30, yPos + 8)
        doc.text(`|`, 80, yPos + 8)
        doc.text(`Affectés: ${item.produitsAffectes || 0}`, 90, yPos + 8)
        doc.text(`|`, 130, yPos + 8)
        doc.text(`Retournés: ${item.produitsRetournes || 0}`, 140, yPos + 8)
        
        doc.setFontSize(9)
        doc.setFont('helvetica', 'normal')
        doc.text(`Total produits listés: ${item.details?.length || 0}`, 105, yPos + 15, { align: 'center' })
        
        // ========== TABLEAU DES PRODUITS ==========
        yPos = 145
        
        if (item.details && item.details.length > 0) {
          const tableData = item.details.map((product, index) => [
            (index + 1).toString(),
            product.referenceInterne || '-',
            product.designation || '-',
            product.categorie || '-',
            product.quantite?.toString() || '1',
            product.imei || product.iccid || '-'
          ])
          
          autoTable(doc, {
            startY: yPos,
            head: [['N°', 'Référence', 'Désignation', 'Catégorie', 'Qté', 'IMEI/ICCID']],
            body: tableData,
            theme: 'grid',
            headStyles: {
              fillColor: [0, 27, 183],
              textColor: 255,
              fontSize: 9,
              fontStyle: 'bold',
              halign: 'center',
              valign: 'middle',
              lineWidth: 0.1,
              lineColor: [255, 255, 255]
            },
            bodyStyles: {
              fontSize: 8,
              textColor: [40, 40, 40],
              lineWidth: 0.1,
              lineColor: [200, 200, 200]
            },
            alternateRowStyles: {
              fillColor: [248, 250, 252]
            },
            columnStyles: {
              0: { cellWidth: 10, halign: 'center' },
              1: { cellWidth: 25, halign: 'center' },
              2: { cellWidth: 50 },
              3: { cellWidth: 25, halign: 'center' },
              4: { cellWidth: 15, halign: 'center' },
              5: { cellWidth: 35, halign: 'center', fontSize: 7 }
            },
            margin: { left: 20, right: 20 }
          })
          
          yPos = doc.lastAutoTable.finalY + 15
        } else {
          doc.setFontSize(10)
          doc.setTextColor(150, 150, 150)
          doc.setFont('helvetica', 'italic')
          doc.text('Aucun produit dans ce bon de sortie', 105, yPos + 20, { align: 'center' })
          yPos += 40
        }
        
        // ========== ESPACES DE SIGNATURE ==========
        // Vérifier s'il reste assez d'espace, sinon nouvelle page
        if (yPos > 220) {
          doc.addPage()
          yPos = 30
        }
        
        const signatureY = Math.max(yPos, 230)
        
        // Cadre signature magasinier
        doc.setDrawColor(16, 185, 129)
        doc.setLineWidth(0.5)
        doc.roundedRect(20, signatureY, 75, 40, 3, 3, 'S')
        
        doc.setFontSize(10)
        doc.setTextColor(16, 185, 129)
        doc.setFont('helvetica', 'bold')
        doc.text('SIGNATURE MAGASINIER', 57.5, signatureY + 8, { align: 'center' })
        
        doc.setFontSize(8)
        doc.setTextColor(100, 100, 100)
        doc.setFont('helvetica', 'normal')
        doc.text('Nom et signature', 57.5, signatureY + 35, { align: 'center' })
        
        // Cadre signature technicien
        doc.setDrawColor(0, 27, 183)
        doc.setLineWidth(0.5)
        doc.roundedRect(115, signatureY, 75, 40, 3, 3, 'S')
        
        doc.setFontSize(10)
        doc.setTextColor(0, 27, 183)
        doc.setFont('helvetica', 'bold')
        doc.text('SIGNATURE TECHNICIEN', 152.5, signatureY + 8, { align: 'center' })
        
        doc.setFontSize(8)
        doc.setTextColor(100, 100, 100)
        doc.setFont('helvetica', 'normal')
        doc.text('Nom et signature', 152.5, signatureY + 35, { align: 'center' })
        
        // ========== PIED DE PAGE ==========
        doc.setDrawColor(200, 200, 200)
        doc.setLineWidth(0.3)
        doc.line(20, 280, 190, 280)
        
        doc.setFontSize(8)
        doc.setTextColor(120, 120, 120)
        doc.setFont('helvetica', 'italic')
        doc.text('Ce document certifie la sortie des produits listés ci-dessus du magasin.', 105, 285, { align: 'center' })
        doc.text('Amana Management Suite - Gestion de Stock', 105, 290, { align: 'center' })
          
        // Sauvegarder le PDF avec nom du technicien
        const fileName = `bon_sortie_${item.technicien.nom}_${item.technicien.prenom}_${dateNow.toISOString().split('T')[0]}.pdf`
        doc.save(fileName)
        
        console.log('✅ Bon de sortie PDF exporté avec succès pour', item.technicien.nom)
      } catch (error) {
        console.error('❌ Erreur détaillée lors de l\'export PDF:', error)
        console.error('Stack trace:', error.stack)
        console.error('Message:', error.message)
        alert(`Erreur lors de l'export PDF: ${error.message}. Veuillez vérifier la console pour plus de détails.`)
      }
    }

    const exportRetourPDF = (item) => {
      try {
        const doc = new jsPDF()
        const dateNow = new Date()
        
        // EN-TÊTE
        doc.setFontSize(28)
        doc.setTextColor(0, 27, 183)
        doc.setFont('helvetica', 'bold')
        doc.text('AMANA', 105, 20, { align: 'center' })
        doc.setFontSize(10)
        doc.setTextColor(100, 100, 100)
        doc.setFont('helvetica', 'normal')
        doc.text('MANAGEMENT SUITE', 105, 27, { align: 'center' })
        doc.setDrawColor(0, 27, 183)
        doc.setLineWidth(0.5)
        doc.line(20, 32, 190, 32)
        
        // TITRE
        doc.setFontSize(20)
        doc.setTextColor(0, 27, 183)
        doc.setFont('helvetica', 'bold')
        doc.text('BON DE RETOUR', 105, 42, { align: 'center' })
        
        const bonNumber = `BR-${dateNow.getFullYear()}${String(dateNow.getMonth() + 1).padStart(2, '0')}${String(dateNow.getDate()).padStart(2, '0')}-${String(Math.floor(Math.random() * 9999)).padStart(4, '0')}`
        doc.setFontSize(10)
        doc.setTextColor(60, 60, 60)
        doc.setFont('helvetica', 'normal')
        doc.text(`N° ${bonNumber}`, 105, 50, { align: 'center' })
        doc.text(`Date: ${dateNow.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}`, 105, 56, { align: 'center' })
        
        let yPos = 70
        
        // TECHNICIEN
        doc.setFillColor(240, 248, 255)
        doc.roundedRect(20, yPos, 85, 35, 3, 3, 'F')
        doc.setDrawColor(0, 27, 183)
        doc.setLineWidth(0.3)
        doc.roundedRect(20, yPos, 85, 35, 3, 3, 'S')
        doc.setFontSize(11)
        doc.setTextColor(0, 27, 183)
        doc.setFont('helvetica', 'bold')
        doc.text('TECHNICIEN', 25, yPos + 7)
        doc.setFontSize(10)
        doc.setTextColor(40, 40, 40)
        doc.text(`${item.technicien.prenom || ''} ${item.technicien.nom || ''}`, 25, yPos + 15)
        doc.setFontSize(9)
        doc.setTextColor(80, 80, 80)
        doc.setFont('helvetica', 'normal')
        doc.text(`Email: ${item.technicien.email || 'N/A'}`, 25, yPos + 22)
        
        // MAGASINIER
        const user = store.state.auth.user || {}
        doc.setFillColor(240, 255, 240)
        doc.roundedRect(110, yPos, 80, 35, 3, 3, 'F')
        doc.setDrawColor(16, 185, 129)
        doc.roundedRect(110, yPos, 80, 35, 3, 3, 'S')
        doc.setFontSize(11)
        doc.setTextColor(16, 185, 129)
        doc.setFont('helvetica', 'bold')
        doc.text('MAGASINIER', 115, yPos + 7)
        doc.setFontSize(10)
        doc.setTextColor(40, 40, 40)
        doc.text(`${user.prenom || ''} ${user.nom || ''}`.trim() || 'Utilisateur', 115, yPos + 15)
        doc.setFontSize(9)
        doc.setTextColor(80, 80, 80)
        doc.setFont('helvetica', 'normal')
        doc.text(`Email: ${user.email || 'N/A'}`, 115, yPos + 22)
        doc.text(`Rôle: ${user.role === 'Admin' ? 'ADMINISTRATEUR' : 'MAGASINIER'}`, 115, yPos + 28)
        
        // STATS
        yPos = 115
        doc.setFillColor(240, 248, 255)
        doc.roundedRect(20, yPos, 170, 20, 3, 3, 'F')
        doc.setDrawColor(0, 27, 183)
        doc.roundedRect(20, yPos, 170, 20, 3, 3, 'S')
        doc.setFontSize(10)
        doc.setTextColor(60, 60, 60)
        doc.setFont('helvetica', 'bold')
        doc.text(`Total: ${item.totalProduits || 0} | Affectés: ${item.parStatut?.affecte_technicien || 0} | En cours: ${item.parStatut?.en_cours || 0}`, 105, yPos + 12, { align: 'center' })
        
        // TABLEAU
        yPos = 145
        if (item.details && item.details.length > 0) {
          const tableData = item.details.map((p, i) => [(i + 1).toString(), p.referenceInterne || '-', p.designation || '-', p.categorie || '-', p.quantite?.toString() || '1', p.imei || p.iccid || '-'])
          autoTable(doc, {
            startY: yPos,
            head: [['N°', 'Référence', 'Désignation', 'Catégorie', 'Qté', 'IMEI/ICCID']],
            body: tableData,
            theme: 'grid',
            headStyles: { fillColor: [0, 27, 183], textColor: 255, fontSize: 9, fontStyle: 'bold', halign: 'center' },
            bodyStyles: { fontSize: 8, textColor: [40, 40, 40] },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            columnStyles: { 0: { cellWidth: 10, halign: 'center' }, 1: { cellWidth: 25, halign: 'center' }, 2: { cellWidth: 50 }, 3: { cellWidth: 25, halign: 'center' }, 4: { cellWidth: 15, halign: 'center' }, 5: { cellWidth: 35, halign: 'center' } },
            margin: { left: 20, right: 20 }
          })
          yPos = doc.lastAutoTable.finalY + 15
        }
        
        // SIGNATURES
        if (yPos > 220) { doc.addPage(); yPos = 30 }
        const signatureY = Math.max(yPos, 230)
        doc.setDrawColor(16, 185, 129)
        doc.roundedRect(20, signatureY, 75, 40, 3, 3, 'S')
        doc.setFontSize(10)
        doc.setTextColor(16, 185, 129)
        doc.setFont('helvetica', 'bold')
        doc.text('SIGNATURE MAGASINIER', 57.5, signatureY + 8, { align: 'center' })
        doc.setDrawColor(0, 27, 183)
        doc.roundedRect(115, signatureY, 75, 40, 3, 3, 'S')
        doc.setTextColor(0, 27, 183)
        doc.text('SIGNATURE TECHNICIEN', 152.5, signatureY + 8, { align: 'center' })
        
        // PIED DE PAGE
        doc.setDrawColor(200, 200, 200)
        doc.line(20, 280, 190, 280)
        doc.setFontSize(8)
        doc.setTextColor(120, 120, 120)
        doc.setFont('helvetica', 'italic')
        doc.text('Ce document certifie le retour des produits listés ci-dessus au magasin.', 105, 285, { align: 'center' })
        doc.text('Amana Management Suite - Gestion de Stock', 105, 290, { align: 'center' })
        
        const fileName = `bon_retour_${item.technicien.nom}_${item.technicien.prenom}_${dateNow.toISOString().split('T')[0]}.pdf`
        doc.save(fileName)
        console.log('✅ Bon de retour PDF exporté avec succès pour', item.technicien.nom)
      } catch (error) {
        console.error('❌ Erreur export PDF retour:', error)
        alert(`Erreur lors de l'export PDF: ${error.message}`)
      }
    }

    return {
      activeTab,
      filters,
      bonsSortie,
      bonsRetour,
      filteredBonsSortie,
      filteredBonsRetour,
      applyFilters,
      getStatusClass,
      getStatusLabel,
      getTypeLabel,
      formatDate,
      formatPrice,
      viewBon,
      signBon,
      verifyBon,
      printBon,
      exportTechnicienPDF,
      exportRetourPDF,
      expandedBons,
      toggleDetails,
      getStatusIcon,
      getStatusIconClass,
      formatEmplacement,
      createReturnBon,
      returnToStock,
      showBonRetourModal,
      openBonRetourModal,
      closeBonRetourModal,
      onBonRetourCreated,
      selectedProductsByTech,
      toggleSelectAllProducts,
      areAllProductsSelected,
      getSelectedProductsCount,
      exportSelectedProductsPDF,
      showMatriculeModal,
      matriculeVoiture,
      closeMatriculeModal,
      confirmMatricule,
      onMatriculeConfirm
    }
  }
}
</script>

<style scoped>
.bons-manager {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

/* Structure Rapport Technicien */
.report-content {
  padding: 1rem;
}

.technicien-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.98) 100%);
  border: 1px solid rgba(226, 232, 240, 0.6);
  border-radius: 20px;
  margin-bottom: 1.5rem;
  overflow: hidden;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  animation: fadeInUp 0.6s ease-out;
}

.technicien-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  cursor: pointer;
  transition: background 0.3s ease;
  position: relative;
}

.card-header::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 1.5rem;
  right: 1.5rem;
  height: 1px;
  background: linear-gradient(90deg, rgba(226, 232, 240, 0.6), transparent);
}

.card-header:hover {
  background: rgba(0, 27, 183, 0.02);
}

.tech-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.tech-avatar {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  box-shadow: 0 4px 15px rgba(0, 27, 183, 0.3);
}

.tech-details h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.tech-details p {
  margin: 0.25rem 0 0 0;
  font-size: 0.85rem;
  color: #64748b;
}

.tech-stats {
  display: flex;
  gap: 1.5rem;
  align-items: center;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.6) 0%, rgba(248, 250, 252, 0.8) 100%);
  border-radius: 10px;
  border: 1px solid rgba(226, 232, 240, 0.4);
  transition: all 0.3s ease;
}

.stat-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
}

.stat-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  color: white;
  flex-shrink: 0;
}

.stat-icon.en-sac {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  box-shadow: 0 4px 15px rgba(245, 158, 11, 0.3);
}

.stat-icon.affectes {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
}

.stat-icon.retournes {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
}

.stat-icon.export-pdf {
  background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
  box-shadow: 0 4px 15px rgba(220, 38, 38, 0.3);
}

.stat-export-btn {
  cursor: pointer;
  border: 1px solid rgba(220, 38, 38, 0.2);
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.05) 0%, rgba(239, 68, 68, 0.05) 100%);
  position: relative;
  overflow: hidden;
}

.stat-export-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(220, 38, 38, 0.1), transparent);
  transition: left 0.5s ease;
}

.stat-export-btn:hover::before {
  left: 100%;
}

.stat-export-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(220, 38, 38, 0.25);
  border-color: rgba(220, 38, 38, 0.4);
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.08) 0%, rgba(239, 68, 68, 0.08) 100%);
}

.stat-export-btn:active {
  transform: translateY(-1px);
}

.stat-export-btn .stat-label {
  font-weight: 600;
  color: #dc2626;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.stat-icon.retour-total {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  box-shadow: 0 3px 10px rgba(139, 92, 246, 0.3);
}

.stat-icon.retour-affecte {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  box-shadow: 0 3px 10px rgba(16, 185, 129, 0.3);
}

.stat-icon.retour-encours {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  box-shadow: 0 3px 10px rgba(245, 158, 11, 0.3);
}

.stat-icon.retour-reserve {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  box-shadow: 0 3px 10px rgba(59, 130, 246, 0.3);
}

.stat-icon.status-pending {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  box-shadow: 0 4px 15px rgba(245, 158, 11, 0.3);
}

.stat-icon.status-signed {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
}

.stat-icon.status-partial {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
}

.stat-icon.status-complete {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  box-shadow: 0 4px 15px rgba(139, 92, 246, 0.3);
}

.stat-icon.status-cancelled {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3);
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1;
}

.stat-label {
  font-size: 0.65rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 0.2rem;
  font-weight: 500;
}

.toggle-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.1) 0%, rgba(0, 70, 255, 0.1) 100%);
  color: var(--primary-blue);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.toggle-btn:hover {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.2) 0%, rgba(0, 70, 255, 0.2) 100%);
  transform: scale(1.1);
}

.card-details {
  padding: 0 1.5rem 1.5rem 1.5rem;
  animation: slideDown 0.3s ease-out;
}

.details-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(226, 232, 240, 0.5);
}

.details-header h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.details-header h4 i {
  color: var(--primary-blue);
}

.details-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-create {
  padding: 0.6rem 1.2rem;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-create:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.btn-return-stock {
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
}

.btn-return-stock:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
}

.btn-return-stock:active {
  transform: translateY(0);
}

.btn-return-stock i {
  font-size: 0.9rem;
}

.no-products {
  text-align: center;
  padding: 3rem 1rem;
  color: #94a3b8;
}

.no-products i {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.no-products p {
  margin: 0;
  font-size: 0.95rem;
}

.products-table {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.6);
}

.products-table table {
  width: 100%;
  border-collapse: collapse;
}

.products-table thead {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.05) 0%, rgba(0, 70, 255, 0.05) 100%);
}

.products-table th {
  padding: 1rem;
  text-align: left;
  font-size: 0.8rem;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid rgba(226, 232, 240, 0.6);
}

.products-table tbody tr {
  transition: background 0.2s ease;
  border-bottom: 1px solid rgba(226, 232, 240, 0.3);
}

.products-table tbody tr:hover {
  background: rgba(0, 27, 183, 0.02);
}

.products-table td {
  padding: 1rem;
  font-size: 0.9rem;
  color: #1e293b;
}

.category-badge {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.1) 0%, rgba(0, 70, 255, 0.1) 100%);
  color: var(--primary-blue);
  border: 1px solid rgba(0, 27, 183, 0.2);
}

.quantity-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  padding: 0.35rem 0.75rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 700;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%);
  color: #059669;
  border: 1px solid rgba(16, 185, 129, 0.2);
}

.imei-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.8rem;
}

.imei-info span {
  color: #64748b;
}

.imei-info strong {
  color: #475569;
  font-weight: 600;
}

.status-badge {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
}

.status-badge.status-assigned {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%);
  color: #059669;
  border: 1px solid rgba(16, 185, 129, 0.2);
}

.status-badge.status-progress {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%);
  color: #d97706;
  border: 1px solid rgba(245, 158, 11, 0.2);
}

.status-badge.status-installed {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%);
  color: #2563eb;
  border: 1px solid rgba(59, 130, 246, 0.2);
}

.status-badge.status-default {
  background: linear-gradient(135deg, rgba(100, 116, 139, 0.1) 0%, rgba(71, 85, 105, 0.1) 100%);
  color: #475569;
  border: 1px solid rgba(100, 116, 139, 0.2);
}

/* Transition Slide */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease-out;
  max-height: 1000px;
  overflow: hidden;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
}

/* Animation slideDown */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.tabs-header {
  display: flex;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.tab-btn {
  flex: 1;
  padding: 1rem 1.5rem;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-weight: 500;
  color: #6c757d;
  transition: all 0.3s ease;
  position: relative;
}

.tab-btn.active {
  color: #007bff;
  background: white;
  border-bottom: 3px solid #007bff;
}

.tab-btn:hover {
  background: rgba(0, 123, 255, 0.1);
}

.badge {
  background: #dc3545;
  color: white;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  min-width: 20px;
  text-align: center;
}

.tab-content {
  padding: 1.5rem;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.toolbar-left {
  display: flex;
  gap: 0.5rem;
}

.toolbar-right {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.btn-export-pdf {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(220, 38, 38, 0.3);
  position: relative;
  overflow: hidden;
}

.btn-export-pdf::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.btn-export-pdf:hover::before {
  left: 100%;
}

.btn-export-pdf:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(220, 38, 38, 0.4);
}

.btn-export-pdf:active {
  transform: translateY(0);
}

.btn-export-pdf i {
  font-size: 1rem;
}

.filter-select {
  padding: 8px 12px;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  background: white;
  font-size: 14px;
  cursor: pointer;
}

.bons-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 2rem;
  padding: 1rem 0;
}

.bon-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.98) 100%);
  border: 1px solid rgba(226, 232, 240, 0.6);
  border-radius: 20px;
  padding: 2rem;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  animation: fadeInUp 0.6s ease-out;
}

.bon-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6px;
  border-radius: 20px 20px 0 0;
}

.bon-card::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.sortie-card::before {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 50%, #004085 100%);
  box-shadow: 0 2px 10px rgba(0, 123, 255, 0.3);
}

.retour-card::before {
  background: linear-gradient(135deg, #28a745 0%, #1e7e34 50%, #155724 100%);
  box-shadow: 0 2px 10px rgba(40, 167, 69, 0.3);
}

.bon-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  border-color: rgba(59, 130, 246, 0.3);
}

.bon-card:hover::after {
  opacity: 1;
}

.sortie-card:hover {
  box-shadow: 0 20px 40px rgba(0, 123, 255, 0.2);
}

.retour-card:hover {
  box-shadow: 0 20px 40px rgba(40, 167, 69, 0.2);
}

.bon-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(226, 232, 240, 0.5);
  position: relative;
}

.bon-header::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 60px;
  height: 2px;
  background: linear-gradient(90deg, #3b82f6, transparent);
  border-radius: 1px;
}

.bon-number {
  font-size: 1.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
  position: relative;
}

.bon-number::before {
  content: '#';
  opacity: 0.6;
  margin-right: 0.25rem;
}

.bon-status {
  padding: 0.6rem 1.2rem;
  border-radius: 25px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.bon-status::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  transition: left 0.5s ease;
}

.bon-status:hover::before {
  left: 100%;
}

.bon-info {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-top: 0.5rem;
  margin-bottom: 1rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 1rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.6) 0%, rgba(248, 250, 252, 0.8) 100%);
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.4);
  transition: all 0.3s ease;
}

.info-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  border-color: rgba(59, 130, 246, 0.3);
}

.info-item label {
  font-size: 0.7rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 0.5rem;
  position: relative;
}

.info-item label::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 20px;
  height: 1px;
  background: linear-gradient(90deg, #3b82f6, transparent);
}

.info-item div {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.4;
}

.technician {
  font-weight: 700;
  color: #475569;
  background: linear-gradient(135deg, #475569 0%, #64748b 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.value {
  font-size: 1.1rem;
  font-weight: 800;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
}

.bon-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding-top: 1.5rem;
  margin-top: 1rem;
  border-top: 1px solid rgba(226, 232, 240, 0.5);
  position: relative;
}

.bon-actions::before {
  content: '';
  position: absolute;
  top: -1px;
  right: 0;
  width: 80px;
  height: 1px;
  background: linear-gradient(90deg, transparent, #3b82f6);
}

.btn-action {
  width: 42px;
  height: 42px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  font-size: 1rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.btn-action::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  transition: left 0.5s ease;
}

.btn-action:hover::before {
  left: 100%;
}

.btn-view { 
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
  color: white;
}

.btn-sign { 
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}

.btn-edit { 
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
}

.btn-delete { 
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.btn-action:hover {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.btn-view:hover {
  box-shadow: 0 8px 25px rgba(6, 182, 212, 0.4);
}

.btn-sign:hover {
  box-shadow: 0 8px 25px rgba(245, 158, 11, 0.4);
}

.btn-edit:hover {
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
}

.btn-delete:hover {
  box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
}

.btn-action:active {
  transform: translateY(-1px) scale(1.02);
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* Styles spécifiques pour les filtres de statuts des bons (Violet) */
.filter-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.95) 100%);
  border: 2px solid rgba(226, 232, 240, 0.6);
  border-radius: 12px;
  padding: 0;
  backdrop-filter: blur(10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.filter-status-bons {
  border-color: rgba(156, 39, 176, 0.15);
  background: linear-gradient(135deg, rgba(156, 39, 176, 0.05) 0%, rgba(142, 36, 170, 0.05) 100%);
}

.filter-status-bons:hover {
  border-color: rgba(156, 39, 176, 0.3);
  box-shadow: 0 8px 32px rgba(156, 39, 176, 0.15);
  transform: translateY(-2px);
}

.filter-status-bons:focus-within {
  border-color: #9c27b0;
  box-shadow: 0 0 0 4px rgba(156, 39, 176, 0.1), 0 8px 32px rgba(156, 39, 176, 0.2);
  transform: translateY(-3px);
}

.filter-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  margin: 4px;
  font-size: 1rem;
  color: white;
  flex-shrink: 0;
}

.filter-status-bons .filter-icon {
  background: linear-gradient(135deg, #9c27b0 0%, #8e24aa 100%);
}

.premium-select {
  border: none;
  background: transparent;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  outline: none;
  cursor: pointer;
  flex: 1;
  min-width: 0;
  appearance: none;
  transition: all 0.3s ease;
}

.filter-status-bons .premium-select {
  color: #9c27b0;
}

.filter-status-bons .premium-select:focus {
  color: #7b1fa2;
}

.filter-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-right: 8px;
  transition: all 0.3s ease;
  pointer-events: none;
}

.filter-status-bons .filter-arrow {
  color: #9c27b0;
}

.filter-status-bons:hover .filter-arrow {
  color: #7b1fa2;
}

.filter-status-bons:focus-within .filter-arrow {
  transform: rotate(180deg);
}

/* Styles pour les boutons "Nouveau Bon" */
.btn-nouveau-bon {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  border: 1px solid #007bff;
  position: relative;
  overflow: hidden;
  font-weight: 600;
  letter-spacing: 0.02em;
  box-shadow: 0 4px 15px rgba(0, 123, 255, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-nouveau-bon::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  transition: left 0.5s ease;
}

.btn-nouveau-bon:hover::before {
  left: 100%;
}

.btn-nouveau-bon:hover {
  background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
  border-color: #0056b3;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 123, 255, 0.4);
}

.btn-nouveau-bon:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
}

.btn-nouveau-bon i {
  margin-right: 0.5rem;
  font-size: 0.9rem;
}

/* Animations professionnelles */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

@keyframes slideInRight {
  0% {
    opacity: 0;
    transform: translateX(30px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes bounceIn {
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes shimmer {
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
}

/* Amélioration des onglets */
.tabs {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.95) 100%);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 0.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(226, 232, 240, 0.6);
}

.tab-button {
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.tab-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  transition: left 0.5s ease;
}

.tab-button:hover::before {
  left: 100%;
}

.tab-button.active {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
  transform: translateY(-1px);
}

.tab-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
}

/* Amélioration de la toolbar */
.toolbar {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.95) 100%);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(226, 232, 240, 0.6);
  position: relative;
  overflow: hidden;
}

.toolbar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6, #06b6d4);
  border-radius: 16px 16px 0 0;
}

/* Animation de chargement pour les cartes */
.bon-card:nth-child(1) { animation-delay: 0.1s; }
.bon-card:nth-child(2) { animation-delay: 0.2s; }
.bon-card:nth-child(3) { animation-delay: 0.3s; }
.bon-card:nth-child(4) { animation-delay: 0.4s; }
.bon-card:nth-child(5) { animation-delay: 0.5s; }
.bon-card:nth-child(6) { animation-delay: 0.6s; }

@media (max-width: 768px) {
  .tech-stats {
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
  }
  
  .stat-item,
  .stat-export-btn {
    width: 100%;
  }
  
  .stat-export-btn .stat-content {
    flex: 1;
    text-align: left;
  }
  
  .card-header {
    flex-direction: column;
    gap: 1rem;
  }
  
  .tech-info {
    width: 100%;
  }
  
  .details-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
  
  .details-actions {
    width: 100%;
    flex-direction: column;
  }
  
  .details-actions .btn-action {
    width: 100%;
    justify-content: center;
  }
  
  .products-table {
    font-size: 0.8rem;
  }
  
  .products-table th,
  .products-table td {
    padding: 0.75rem 0.5rem;
  }
  
  .toolbar {
    flex-direction: column;
    align-items: stretch;
  }
}

/* Styles pour la sélection de produits */
.selection-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 8px;
  margin-bottom: 1rem;
  border: 1px solid #e2e8f0;
}

.select-all-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
}

.select-all-checkbox input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #3b82f6;
}

.product-checkbox {
  width: 16px;
  height: 16px;
  accent-color: #3b82f6;
  cursor: pointer;
}

.products-table th:first-child {
  text-align: center;
  padding: 0.5rem;
}

.products-table td:first-child {
  text-align: center;
  padding: 0.5rem;
}

.btn-sm {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn-primary.btn-sm {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.btn-primary.btn-sm:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.btn-primary.btn-sm:active {
  transform: translateY(0);
}
</style>
